import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../middleware/dataSlice';

export interface UserInfo {
    companyId: number;
    companyName: string;
    email: string;
    selectedUserInfo: number;
    isUsedMealCard?: boolean | null;
    fastMaxVal?: number | null;
}
export interface Wallet {
    id: number;
    customerId: number;
    currencyId: number;
    walletNo: string;
    name: string;
    status: boolean;
    statusDate: string;
    messageId: string | null;
    cDate: string;
    balance: number;
    iban: string;
}

export interface WalletResponse extends Array<Wallet> { }

export interface BalanceDetail {
    balanceType: number;
    description: string;
    balance: number;
    convertedBalance: number;
}

export interface WalletBalance {
    id: number;
    name: string;
    totalBalance: number;
    cashableBalance: number;
    balanceDetails: BalanceDetail[];
}

export interface BalanceResponse {
    data: {
        customerId: number;
        totalBalance: number;
        cashableBalance: number;
        wallets: WalletBalance[];
    };
    success: boolean;
    message: string | null;
    statusCode: number;
}

interface Items {
    code: string;
    description: string;
    townCode: number;
    id?: number;
    value?: number;
    neighborhoodName: string;
    text?: string;
    name?: string;
}

export interface ApiResponseData {
    data: Items[];
    success: boolean;
    message?: string;
}

export interface User {
    id: string;
    userName: string;
    email: string;
    phoneNumber: string;
    company: string;
    status: boolean;
    cDate: string;
}

export interface GetUsersResponse {
    data: User[];
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface Role {
    id: string;
    name: string;
    checker: boolean;
    roles: string[];
    status: boolean;
}

export interface GetRolesForPageResponse {
    data: Role[];
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface UserRole {
    userId: string;
    roles: string[];
}

export interface GetRolesToUserResponse {
    data: UserRole;
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface AssignRoleToUserRequest {
    userId: string;
    roles: string[];
}

export interface AssignRoleToUserResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface AddUserRequest {
    email: string;
    password: string;
    companyId: number;
    ip: string;
    name: string;
    surName: string;
    companyList: string[];
}

export interface AddUserResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface AddRoleRequest {
    roleName: string;
    companyName: string;
    checker: boolean;
    maker: boolean;
    roles: string[];
}

export interface AddRoleResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface UpdateRoleRequest {
    roleName: string;
    companyName: string;
    checker: boolean;
    maker: boolean;
    roles: string[];
    id: string;
}

export interface UpdateRoleResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface RemoveRoleResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface Company {
    customerId: number;
    name: string | null;
    surName: string | null;
    phoneNumber: string | null;
    title: string;
    address: string | null;
    taxOffice: string | null;
    taxNo: string | null;
    applicationDate: string;
    applicationStatus: number;
}

export interface GetCompanyListResponse {
    data: Company[];
    success: boolean;
    message: string | null;
    statusCode: number;
}

export interface UpdateCompanyUserStatusRequest {
    companyId: number;
    customerId: string;
    isActive: boolean;
}

export interface UpdateCompanyUserStatusResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface DeActiveCompanyUserRequest {
    companyId: number;
    customerId: string;
    makingCustomerId: number;
}

export interface DeActiveCompanyUserResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface RequestUpdatePhoneOrEmailRequest {
    NewValue: string;
    IsEmail: boolean;
    CustomerId: number;
}

export interface VerifyUpdatePhoneOrEmailRequest {
    Code: string;
    CustomerId: number;
}

export interface RequestUpdatePhoneOrEmailResponse {
    data?: { expireAtSeconds?: number };
    success: boolean;
    message?: string;
    statusCode: number;
}

export interface VerifyUpdatePhoneOrEmailResponse {
    success: boolean;
    message?: string;
    statusCode: number;
}


export const componentAPI = createApi({
    reducerPath: 'componentAPI',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getAllData: builder.mutation<any[], { filter?: Record<string, any>, method: string, url: string }>({
            query: ({ filter, method, url }) => {
                if (method === 'GET') {
                    // GET metodu için parametreler zaten URL'de olmalı
                    return {
                        url,
                        method,
                    };
                } else {
                    // POST metodu için body kullan
                    return {
                        url,
                        method,
                        body: filter,
                    };
                }
            },
        }),
        fetchData: builder.query<any, { filter?: Record<string, any>, method: string, url: string }>({
            query: ({ filter, method, url }) => ({
                url,
                method,
                body: filter,
            }),
        }),
        hasRolePermissionToComponent: builder.mutation<any, { code: string }>({
            query: (payload) => ({
                url: `/Authorization/HasRolePermissionToComponent`,
                method: 'POST',
                body: payload
            })
        }),
        getUserInfo: builder.mutation<UserInfo[], void>({
            query: () => ({
                url: `/User/GetUserInfo`,
                method: 'GET',
            }),
            transformResponse: (response: { data: UserInfo[], success: boolean, message: string | null, statusCode: number }) => {
                return response.data || [];
            }
        }),
        getBalance: builder.query<BalanceResponse, number>({
            query: (companyId) => ({
                url: `/Company/GetBalance?companyId=${companyId}`,
                method: 'GET',
            })
        }),
        getWallet: builder.query<WalletResponse, number>({
            query: (customerId) => ({
                url: `/Company/GetWallet?customerId=${customerId}`,
                method: 'GET',
            })
        }),
        ProcessTypeLookup: builder.query<ApiResponseData, void>({
            query: () => ({
                url: '/Process/ProcessTypeLookup',
                method: 'GET',
            }),
        }),
        ProcessStatusLookup: builder.query<ApiResponseData, void>({
            query: () => ({
                url: '/Process/ProcessStatusLookup',
                method: 'GET',
            }),
        }),
        ProcessMakingCustomer: builder.query<ApiResponseData, { companyId: number }>({
            query: ({ companyId }) => ({
                url: `/Process/ProcessMakingCustomer?companyId=${companyId}`,
                method: 'GET',
            }),
        }),
        getCities: builder.query<ApiResponseData, void>({
            query: () => ({
                url: '/City/getcities',
                method: 'GET',
            }),
        }),
        getCounties: builder.query<ApiResponseData, { cityId: number }>({
            query: ({ cityId }) => ({
                url: `/City/getcounties?cityId=${cityId}`,
                method: 'POST',
            }),
        }),
        getNeighborhoods: builder.query<ApiResponseData, { countyId: number }>({
            query: ({ countyId }) => ({
                url: `/City/getneighborhoods?countyId=${countyId}`,
                method: 'GET',
            }),
        }),
        getRolesForPage: builder.query<GetRolesForPageResponse, { companyId: number }>({
            query: ({ companyId }) => ({
                url: `/Roles/GetRolesForPage?companyName=${companyId}`,
                method: 'GET',
            }),
        }),
        getRolesToUser: builder.mutation<GetRolesToUserResponse, { userId: string }>({
            query: (payload) => ({
                url: `/Authorization/GetRolesToUser`,
                method: 'POST',
                body: payload,
            }),
        }),
        assignRoleToUser: builder.mutation<AssignRoleToUserResponse, AssignRoleToUserRequest>({
            query: (payload) => ({
                url: `/Authorization/AssignRoleToUser`,
                method: 'POST',
                body: payload,
            }),
        }),
        addUser: builder.mutation<AddUserResponse, AddUserRequest>({
            query: (payload) => ({
                url: `/User/addUser`,
                method: 'POST',
                body: payload,
            }),
        }),
        getCompanyList: builder.query<GetCompanyListResponse, { customerId: number }>({
            query: ({ customerId }) => ({
                url: `/Company/GetCompanyList?customerId=${customerId}`,
                method: 'GET',
            }),
        }),
        addRole: builder.mutation<AddRoleResponse, AddRoleRequest>({
            query: (payload) => ({
                url: `/Roles/AddRole`,
                method: 'POST',
                body: payload,
            }),
        }),
        updateRole: builder.mutation<UpdateRoleResponse, UpdateRoleRequest>({
            query: (payload) => ({
                url: `/Roles/UpdateRole`,
                method: 'POST',
                body: payload,
            }),
        }),
        removeRole: builder.query<RemoveRoleResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/Roles/RemoveRole?id=${id}`,
                method: 'GET',
            }),
        }),
        multipleSendStatusLookup: builder.query<ApiResponseData, void>({
            query: () => ({
                url: '/Process/MultipleSendStatusLookup',
                method: 'GET',
            }),
        }),
        enumProductCodeLookup: builder.query<ApiResponseData, void>({
            query: () => ({
                url: '/Card/enumProductCodeLookup',
                method: 'GET',
            }),
        }),
        updateCompanyUserStatus: builder.mutation<UpdateCompanyUserStatusResponse, UpdateCompanyUserStatusRequest>({
            query: (payload) => ({
                url: `/User/updateCompanyUserStatus`,
                method: 'POST',
                body: payload,
            }),
        }),
        deActiveCompanyUser: builder.mutation<DeActiveCompanyUserResponse, DeActiveCompanyUserRequest>({
            query: (payload) => ({
                url: `/User/deActiveCompanyUser`,
                method: 'POST',
                body: payload,
            }),
        }),
        requestUpdatePhoneOrEmail: builder.mutation<RequestUpdatePhoneOrEmailResponse, RequestUpdatePhoneOrEmailRequest>({
            query: (payload) => ({
                url: `/User/RequestUpdatePhoneOrEmail`,
                method: 'POST',
                body: payload,
            }),
        }),
        verifyUpdatePhoneOrEmail: builder.mutation<VerifyUpdatePhoneOrEmailResponse, VerifyUpdatePhoneOrEmailRequest>({
            query: (payload) => ({
                url: `/User/VerifyUpdatePhoneOrEmail`,
                method: 'POST',
                body: payload,
            }),
        }),
        inputOutputLookup: builder.query<any, void>({
            query: () => ({
                url: `/Process/InputOutputLookup`,
                method: 'GET',
            }),
        }),
    })
});

export const {
    useGetAllDataMutation,
    useFetchDataQuery,
    useHasRolePermissionToComponentMutation,
    useGetUserInfoMutation,
    useGetBalanceQuery,
    useGetWalletQuery,
    useProcessTypeLookupQuery,
    useProcessStatusLookupQuery,
    useProcessMakingCustomerQuery,
    useGetCitiesQuery,
    useGetCountiesQuery,
    useGetNeighborhoodsQuery,
    useGetRolesForPageQuery,
    useLazyGetRolesForPageQuery,
    useGetRolesToUserMutation,
    useAssignRoleToUserMutation,
    useAddUserMutation,
    useGetCompanyListQuery,
    useAddRoleMutation,
    useUpdateRoleMutation,
    useLazyRemoveRoleQuery,
    useMultipleSendStatusLookupQuery,
    useEnumProductCodeLookupQuery,
    useUpdateCompanyUserStatusMutation,
    useDeActiveCompanyUserMutation,
    useRequestUpdatePhoneOrEmailMutation,
    useVerifyUpdatePhoneOrEmailMutation,
    useInputOutputLookupQuery,
} = componentAPI;
